import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SasDynamicFormService } from '@shared/sas-dynamic-form/sas-dynamic-form.service';
import { SasLabelValueComponent } from '@shared/sas-label-value/sas-label-value.component';
import { FormSubmittedService } from '@shared/services/form-submitted.service';

@Component({
    selector: 'sas-textarea-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, SasLabelValueComponent],
    template: `
        @if (isShowingSummaryInfo) {
            <sas-label-value [label]="field.props.label ?? ''"
                             [model]="formControl.value"
                             sasClass="textmode toplabel mb-2">
            </sas-label-value>
        } @else {
            <div class="form-group mb-3 position-relative">
                <label [for]="field.id"
                    [id]="field.id + 'Label'"
                    [title]="field.props['helptext'] | translate"
                    class="form-label"
                    [textContent]="field.props.label"
                    translate="{{field.props.label}}">
                </label>
                @if (field.props.required) {
                <span> *</span>
                }
                <div [title]="helpText" class="sas-inline-block">
                    @if (helpText.length && !isReadonly) {
                    <i class="bi bi-info-circle-fill" style="font-size: 15px; cursor: help; margin-left: 5px;"></i>
                    }
                </div>
                @if (field.props.maxLength && !isReadonly) {
                <div class="pull-right">
                    <span class="characters-left">(Characters left: {{ charactersLeft(field.props.maxLength, formControl.value) }})</span>
                </div>
                }
                <textarea [id]="field.id"
                          [name]="field.name"
                          class="form-control"
                          [ngClass]="{'is-invalid' : field.formControl.invalid && (field.formControl.dirty || formSubmittedService.isSubmitted())}"
                          title="{{field.props.placeholder! | translate}}"
                          rows="{{field.props.rows}}"
                          maxlength="{{field.props.maxLength}}"
                          [formControl]="formControl"
                          required="{{field.props.required}}"
                          placeholder="{{field.props.placeholder! | translate}}"
                          [readonly]="isReadonly"
                          role="textbox"
                          tabindex="0"
                          [attr.aria-invalid]="field.formControl.invalid && (field.formControl.dirty || formSubmittedService.isSubmitted())"
                          [attr.aria-readonly]="isReadonly">
                </textarea>
                <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                    <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
                </div>
            </div>
        }
    `,
    styleUrl: './sas-textarea-field-type.component.scss'
})
export class SasTextareaFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    private translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);

    helpText: string = '';
    isShowingSummaryInfo: boolean = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);

            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        this.isShowingSummaryInfo = this.field.props['isShowingSummaryInfo'] ?? false;
    }

    charactersLeft(maxLength: number, currentLength: string) {
        if (currentLength === undefined) {
            return maxLength;
        }
        return maxLength - currentLength.length;
    }
}
